import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Page, PageHeader } from "@/components/Bits";
import api, { API } from "@/lib/api";
import { TOURS, startTour } from "@/lib/tours";
import { FLAT_GUIDES, TOTAL_GUIDES } from "@/lib/helpContent";
import {
  GraduationCap, CheckCircle, Rocket, BookOpen,
  ArrowRight, CircleNotch, PlayCircle, FilmSlate, Play, Sparkle, Trophy, DownloadSimple, LinkedinLogo, Medal, MagnifyingGlass,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { NarratedByLottie } from "@/components/NarratedByLottie";

function Ring({ value, total, label, color, onClick, cta }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  const inner = (
    <>
      <div className="relative w-16 h-16 shrink-0">
        <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#EEF2F6" strokeWidth="3.4" />
          <circle cx="18" cy="18" r="15.9" fill="none" stroke={color} strokeWidth="3.4" strokeLinecap="round"
            strokeDasharray={`${pct}, 100`} />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-navy font-mono">{pct}%</span>
      </div>
      <div>
        <div className="text-2xl font-bold text-navy font-mono leading-none">{value}<span className="text-slate-400 text-lg">/{total}</span></div>
        <div className="text-sm text-slate-600 mt-1">{label}</div>
        {onClick && cta && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-orangeink mt-1.5">
            {cta} <ArrowRight size={13} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
          </span>
        )}
      </div>
    </>
  );
  const cls = "flex items-center gap-4 rounded-2xl border border-slate-200 bg-white shadow-card p-5";
  if (onClick) {
    return (
      <button onClick={onClick} data-testid="learning-guides-ring"
        className={`group text-left w-full ${cls} hover:border-orange hover:shadow-card-hover transition-all`}>
        {inner}
      </button>
    );
  }
  return <div className={cls}>{inner}</div>;
}

export default function Learning() {
  const nav = useNavigate();
  const [progress, setProgress] = useState(null);
  const [videos, setVideos] = useState([]);
  const [activeVideo, setActiveVideo] = useState(null);
  const [ended, setEnded] = useState(false);
  const [path, setPath] = useState(null);
  const [board, setBoard] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState("");
  const [results, setResults] = useState([]);
  const pendingSeekRef = useRef(null);

  useEffect(() => {
    const term = searchQ.trim();
    if (term.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/learning/search?q=${encodeURIComponent(term)}`)
        .then(({ data }) => setResults(data.results || []))
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [searchQ]);

  useEffect(() => {
    if (!activeVideo) { setChapters([]); return; }
    setChapters([]);
    api.get(`/learning/howto/${activeVideo.slug}/chapters`)
      .then(({ data }) => setChapters(data.chapters || []))
      .catch(() => setChapters([]));
  }, [activeVideo]);

  const seekVideo = (t) => {
    const el = videoRef.current;
    if (!el) return;
    el.currentTime = t;
    setEnded(false);
    el.play?.().catch(() => {});
  };

  const fmtChapter = (s) => {
    const sec = Math.max(0, Math.round(s || 0));
    return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
  };

  const nextVideo = activeVideo
    ? videos[(videos.findIndex((v) => v.slug === activeVideo.slug) + 1) % videos.length] || null
    : null;

  const playVideo = (v, seek = null) => { setEnded(false); pendingSeekRef.current = seek; setActiveVideo(v); };

  const openAtMoment = (r) => {
    const v = videos.find((x) => x.slug === r.slug);
    if (!v) return;
    setSearchQ("");
    setResults([]);
    playVideo(v, r.time);
  };

  const load = async () => {
    try {
      const [p, v, lp] = await Promise.all([
        api.get("/learning/progress"),
        api.get("/learning/walkthroughs"),
        api.get("/learning/path"),
      ]);
      setProgress(p.data);
      setVideos(v.data.videos || []);
      setPath(lp.data);
      api.get("/learning/leaderboard").then(({ data }) => setBoard(data)).catch(() => {});
    } catch { /* noop */ } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const guidesDone = useMemo(() => new Set(progress?.guides || []), [progress]);
  const toursDone = useMemo(() => new Set(progress?.tours || []), [progress]);
  const videoDone = useMemo(() => new Set(progress?.video || []), [progress]);

  // Resume = first guide not yet read, then first tour not completed.
  const nextGuide = FLAT_GUIDES.find((g) => !guidesDone.has(g.anchor));
  const nextTour = TOURS.find((t) => !toursDone.has(t.id));

  const openGuide = (anchor) => {
    api.post("/learning/progress", { kind: "guide", key: anchor }).catch(() => {});
    nav(`/help?focus=${encodeURIComponent(anchor)}`);
  };

  const doTour = (id) => startTour(id, { onComplete: load });

  const onVideoPlay = (slug) => {
    if (videoDone.has(slug)) return;
    api.post("/learning/progress", { kind: "video", key: slug }).catch(() => {});
    setProgress((p) => (p ? { ...p, video: [...(p.video || []), slug] } : p));
    setPath((pt) => {
      if (!pt) return pt;
      const stages = pt.stages.map((s) => {
        const items = s.items.map((it) => (it.slug === slug && !it.watched ? { ...it, watched: true } : it));
        return { ...s, items, done: items.filter((it) => it.watched).length };
      });
      const done = stages.reduce((a, s) => a + s.done, 0);
      const complete = pt.total > 0 && done === pt.total;
      return { ...pt, stages, done, percent: pt.total ? Math.round((done / pt.total) * 100) : 0,
               complete, badge: complete ? "Lottie Learner" : null };
    });
  };

  const videoRef = useRef(null);
  const lastSaveRef = useRef(0);
  const savePosition = (slug, position, duration) =>
    api.post("/learning/video-position", {
      slug,
      position: Number.isFinite(position) ? position : 0,
      duration: Number.isFinite(duration) ? duration : 0,
    }).catch(() => {});
  const onTimeUpdate = () => {
    const el = videoRef.current;
    if (!el || !activeVideo) return;
    const now = Date.now();
    if (now - lastSaveRef.current > 5000) {
      lastSaveRef.current = now;
      savePosition(activeVideo.slug, el.currentTime, el.duration);
    }
  };
  const onLoadedMeta = () => {
    const el = videoRef.current;
    if (!el) return;
    if (pendingSeekRef.current != null) {
      el.currentTime = pendingSeekRef.current;
      pendingSeekRef.current = null;
      el.play?.().catch(() => {});
      return;
    }
    if (activeVideo?.position > 5 && activeVideo.position < el.duration - 5) {
      el.currentTime = activeVideo.position;
    }
  };
  const closeVideo = () => {
    const el = videoRef.current;
    if (el && activeVideo) savePosition(activeVideo.slug, el.currentTime, el.duration);
    setActiveVideo(null);
    setTimeout(load, 500);
  };

  if (loading) {
    return (
      <Page>
        <PageHeader overline="Learning" title="Your learning progress" />
        <div className="py-20 text-center text-slate-500" data-testid="learning-loading">
          <CircleNotch size={26} className="animate-spin mx-auto" />
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        overline="Learning Centre"
        title="Your learning progress"
        subtitle="Master Lottie at your own pace — watch the walkthrough videos, work through the how-to guides, and take the interactive tours. We'll remember where you got to."
      />

      {/* Progress rings */}
      <div className="grid gap-4 sm:grid-cols-3 mb-6" data-testid="learning-summary">
        <Ring value={path?.done ?? 0} total={path?.total || 1} label="Videos watched" color="#22C55E" />
        <Ring value={guidesDone.size} total={TOTAL_GUIDES} label="Guides read" color="#F37A24"
          onClick={() => (nextGuide ? openGuide(nextGuide.anchor) : nav("/help"))}
          cta={nextGuide ? "Continue reading" : "Review guides"} />
        <Ring value={toursDone.size} total={TOURS.length} label="Tours completed" color="#2EA3FD" />
      </div>

      {/* Resume */}
      {(nextGuide || nextTour) && (
        <div data-testid="learning-resume"
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-navy text-white shadow-card p-5 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(242,111,33,0.18)" }}>
              <GraduationCap size={22} weight="duotone" style={{ color: "#F37A24" }} />
            </div>
            <div>
              <h3 className="font-heading font-bold">Pick up where you left off</h3>
              <p className="text-white/60 text-sm mt-0.5">
                {nextGuide ? `Next guide: ${nextGuide.title}` : `Next tour: ${nextTour.title}`}
              </p>
            </div>
          </div>
          {nextGuide ? (
            <button onClick={() => openGuide(nextGuide.anchor)} data-testid="learning-resume-btn"
              className="shrink-0 inline-flex items-center justify-center gap-2 font-semibold px-5 py-3 rounded-xl text-white transition-transform hover:scale-105"
              style={{ background: "#F37A24" }}>
              Continue <ArrowRight size={16} weight="bold" />
            </button>
          ) : (
            <button onClick={() => doTour(nextTour.id)} data-testid="learning-resume-btn"
              className="shrink-0 inline-flex items-center justify-center gap-2 font-semibold px-5 py-3 rounded-xl text-white transition-transform hover:scale-105"
              style={{ background: "#F37A24" }}>
              Start tour <ArrowRight size={16} weight="bold" />
            </button>
          )}
        </div>
      )}

      {/* Guided getting-started path with completion badge */}
      {path && path.total > 0 && (
        <section className="mb-10" data-testid="learning-path">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div>
              <h2 className="overline text-orangeink mb-1">Getting started path</h2>
              <p className="text-sm text-slate-600">Work through every walkthrough to master WorkWithLottie.</p>
            </div>
            {path.complete ? (
              <div className="flex items-center gap-2 flex-wrap" data-testid="learning-path-badge">
                <span className="inline-flex items-center gap-2 text-sm font-bold px-3 py-1.5 rounded-full" style={{ background: "rgba(34,197,94,0.12)", color: "#15803D" }}>
                  <Trophy size={16} weight="fill" /> {path.badge} — path complete!
                </span>
                <a href={`${API}/learning/certificate`} download data-testid="cert-download"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-white px-3 py-1.5 rounded-full transition-transform hover:scale-105" style={{ background: "#0E103F" }}>
                  <DownloadSimple size={15} weight="bold" /> Certificate
                </a>
                <a href="https://www.linkedin.com/sharing/share-offsite/?url=https%3A%2F%2Fworkwithlottie.com"
                  target="_blank" rel="noopener noreferrer" data-testid="cert-linkedin"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full border border-slate-200 text-navy hover:border-navy transition-colors">
                  <LinkedinLogo size={15} weight="fill" /> Share
                </a>
              </div>
            ) : (
              <span className="text-sm font-semibold text-navy" data-testid="learning-path-progress">{path.done}/{path.total} watched</span>
            )}
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-5">
            <div className="h-full rounded-full transition-all" style={{ width: `${path.percent}%`, background: path.complete ? "#22C55E" : "#F37A24" }} />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {path.stages.map((s) => (
              <div key={s.category} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`path-stage-${s.category}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-bold text-navy text-sm">{s.category}</div>
                  <div className="text-xs font-semibold text-slate-400">{s.done}/{s.total}</div>
                </div>
                <ul className="space-y-1">
                  {s.items.map((it) => {
                    const v = videos.find((x) => x.slug === it.slug);
                    return (
                      <li key={it.slug}>
                        <button onClick={() => v && playVideo(v)} disabled={!v} data-testid={`path-item-${it.slug}`}
                          className="w-full flex items-center gap-2.5 text-left py-1.5 group disabled:opacity-50">
                          {it.watched
                            ? <CheckCircle size={18} weight="fill" className="text-positive shrink-0" />
                            : <span className="w-[18px] h-[18px] rounded-full border-2 border-slate-300 shrink-0 group-hover:border-orange" />}
                          <span className={`text-sm leading-snug ${it.watched ? "text-slate-500 line-through decoration-slate-300" : "text-navy group-hover:text-orangeink"}`}>{it.title}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Team leaderboard — who's completed the path */}
      {board && board.members > 1 && (
        <section className="mb-10" data-testid="learning-leaderboard">
          <div className="flex items-center gap-2 mb-4">
            <Medal size={18} weight="fill" style={{ color: "#F37A24" }} />
            <h2 className="font-heading font-bold text-navy text-base">Team leaderboard</h2>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            {board.leaderboard.map((r) => (
              <div key={r.rank} data-testid={`leaderboard-row-${r.rank}`}
                className={`flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-0 ${r.is_you ? "bg-orange/5" : ""}`}>
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${r.rank === 1 ? "text-white" : "text-slate-500 bg-slate-100"}`}
                  style={r.rank === 1 ? { background: "#F37A24" } : {}}>{r.rank}</span>
                <span className="flex-1 text-sm font-semibold text-navy truncate">
                  {r.name}{r.is_you && <span className="text-slate-400 font-normal"> (you)</span>}
                </span>
                {r.complete && <Trophy size={16} weight="fill" className="text-positive" data-testid={`leaderboard-badge-${r.rank}`} />}
                <span className="text-sm font-mono tabular-nums text-slate-500 shrink-0">{r.watched}/{r.total}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Walkthrough videos — featured 'start here' + thumbnail gallery */}
      {videos.length > 0 && (() => {
        const featured = videos.find((v) => v.featured);
        const rest = videos.filter((v) => !v.featured);
        const fresh = videos.filter((v) => v.updated_recently);
        const spotlit = videos.filter((v) => v.spotlight);
        return (
          <>
            {spotlit.length > 0 && (
              <div className="mb-8" data-testid="spotlight-strip">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 text-[0.7rem] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full text-white" style={{ background: "#F37A24" }}>
                    <Sparkle size={13} weight="fill" /> Spotlight
                  </span>
                  <span className="text-sm text-slate-500">The walkthroughs most worth your time</span>
                </div>
                <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
                  {spotlit.map((v) => (
                    <button key={v.slug} onClick={() => playVideo(v)} data-testid={`spotlight-${v.slug}`}
                      className="group shrink-0 w-64 text-left rounded-2xl border border-orange/30 bg-white shadow-card overflow-hidden hover:border-orange hover:shadow-card-hover transition-all">
                      <div className="relative aspect-video bg-navy overflow-hidden">
                        {v.has_thumb ? (
                          <img src={`${API}${v.thumb_url}`} alt={v.title} className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <FilmSlate size={32} weight="duotone" style={{ color: "#F37A24" }} />
                          </div>
                        )}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="w-12 h-12 rounded-full flex items-center justify-center transition-transform group-hover:scale-110" style={{ background: "rgba(243,122,36,0.95)" }}>
                            <Play size={20} weight="fill" className="text-white ml-0.5" />
                          </span>
                        </div>
                      </div>
                      <div className="p-3">
                        <p className="font-heading font-bold text-navy text-sm leading-tight line-clamp-2">{v.title}</p>
                        <p className="text-[0.7rem] text-slate-500 mt-1">{v.category}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {fresh.length > 0 && (
              <div className="mb-8" data-testid="whats-new-strip">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 text-[0.7rem] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full text-white" style={{ background: "#00A859" }}>
                    <Sparkle size={13} weight="fill" /> New this week
                  </span>
                  <span className="text-sm text-slate-500">{fresh.length} freshly updated walkthrough{fresh.length === 1 ? "" : "s"}</span>
                </div>
                <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
                  {fresh.map((v) => (
                    <button key={v.slug} onClick={() => playVideo(v)} data-testid={`whats-new-${v.slug}`}
                      className="group shrink-0 w-56 text-left rounded-2xl border border-emerald-200 bg-white shadow-card overflow-hidden hover:border-emerald-400 hover:shadow-card-hover transition-all">
                      <div className="relative aspect-video bg-navy overflow-hidden">
                        {v.has_thumb ? (
                          <img src={`${API}${v.thumb_url}`} alt={v.title} className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <FilmSlate size={32} weight="duotone" style={{ color: "#F37A24" }} />
                          </div>
                        )}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="w-12 h-12 rounded-full flex items-center justify-center transition-transform group-hover:scale-110" style={{ background: "rgba(243,122,36,0.95)" }}>
                            <Play size={20} weight="fill" className="text-white ml-0.5" />
                          </span>
                        </div>
                        <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[0.58rem] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full text-white shadow" style={{ background: "#00A859" }}>
                          <Sparkle size={9} weight="fill" /> Updated
                        </span>
                      </div>
                      <div className="p-3">
                        <p className="font-heading font-bold text-navy text-sm leading-tight line-clamp-2">{v.title}</p>
                        <p className="text-[0.7rem] text-slate-500 mt-1">{v.category}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <h2 className="overline text-orangeink mb-3">Watch the walkthroughs</h2>

            <div className="relative mb-5" data-testid="learning-search">
              <MagnifyingGlass size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                data-testid="learning-search-input"
                placeholder="Search what's said across every walkthrough…"
                className="w-full rounded-xl border border-slate-200 bg-white pl-10 pr-4 py-3 text-sm text-navy placeholder:text-slate-400 focus:outline-none focus:border-orange focus:ring-2 focus:ring-orange/20 transition-shadow" />
              {searchQ.trim().length >= 2 && (
                <div className="absolute z-30 mt-2 w-full rounded-xl border border-slate-200 bg-white shadow-card-hover overflow-hidden" data-testid="learning-search-results">
                  {results.length === 0 ? (
                    <div className="px-4 py-4 text-sm text-slate-500" data-testid="learning-search-empty">
                      No spoken match for "{searchQ.trim()}".
                    </div>
                  ) : (
                    <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
                      {results.map((r, i) => (
                        <button key={`${r.slug}-${r.time}-${i}`} onClick={() => openAtMoment(r)}
                          data-testid={`learning-search-result-${i}`}
                          className="w-full flex items-start gap-3 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors">
                          {r.has_thumb && (
                            <img src={`${API}${r.thumb_url}`} alt="" loading="lazy"
                              className="w-16 h-10 rounded object-cover object-top border border-slate-100 shrink-0" />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-navy truncate">{r.title}</span>
                              <span className="font-mono text-[0.7rem] text-orangeink shrink-0">{fmtChapter(r.time)}</span>
                            </div>
                            <div className="text-xs text-slate-500 line-clamp-2 mt-0.5">{r.snippet}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {featured && (
              <button onClick={() => playVideo(featured)} data-testid="learning-featured"
                className="group w-full text-left rounded-2xl border border-slate-200 bg-white shadow-card overflow-hidden hover:border-orange hover:shadow-card-hover transition-all mb-4 grid sm:grid-cols-2">
                <div className="relative aspect-video bg-navy overflow-hidden">
                  {featured.has_thumb && (
                    <img src={`${API}${featured.thumb_url}`} alt={featured.title}
                      className="w-full h-full object-cover" />
                  )}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="w-16 h-16 rounded-full flex items-center justify-center transition-transform group-hover:scale-110" style={{ background: "rgba(243,122,36,0.95)" }}>
                      <Play size={28} weight="fill" className="text-white ml-1" />
                    </span>
                  </div>
                  {featured.percent > 0 && featured.percent < 98 && (
                    <span className="absolute bottom-0 left-0 h-1.5" data-testid={`video-progress-${featured.slug}`}
                      style={{ width: `${featured.percent}%`, background: "#F37A24" }} />
                  )}
                  {featured.updated_recently && (
                    <span data-testid={`video-updated-${featured.slug}`}
                      className="absolute top-3 right-3 inline-flex items-center gap-1 text-[0.62rem] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full text-white shadow" style={{ background: "#00A859" }}>
                      <Sparkle size={10} weight="fill" /> Updated
                    </span>
                  )}
                </div>
                <div className="p-6 flex flex-col justify-center">
                  <span className="inline-flex items-center gap-1.5 self-start text-[0.7rem] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full mb-3" style={{ background: "rgba(242,111,33,0.12)", color: "#C2410C" }}>
                    <Sparkle size={13} weight="fill" /> Start here
                  </span>
                  <h3 className="font-heading font-bold text-navy text-xl leading-tight">{featured.title}</h3>
                  {featured.excerpt && <p className="text-slate-600 text-sm mt-2 line-clamp-3">{featured.excerpt}</p>}
                  <span className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-orangeink">
                    {featured.percent > 0 ? "Resume walkthrough" : "Watch walkthrough"} <ArrowRight size={16} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </button>
            )}

            <div className="space-y-8 mb-8" data-testid="learning-videos">
              {["Guided workflows", "Getting set up", "Day-to-day", "Month-end", "Administration"].map((cat) => {
                const items = rest.filter((v) => v.category === cat);
                if (!items.length) return null;
                return (
                  <div key={cat} data-testid={`learning-section-${cat}`}>
                    <h3 className="font-heading font-bold text-navy text-base mb-3">{cat}</h3>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {items.map((v) => (
                        <button key={v.slug} onClick={() => playVideo(v)} data-testid={`learning-video-${v.slug}`}
                          className="group text-left rounded-2xl border border-slate-200 bg-white shadow-card overflow-hidden hover:border-orange hover:shadow-card-hover transition-all">
                          <div className="relative aspect-video bg-navy overflow-hidden">
                            {v.has_thumb ? (
                              <img src={`${API}${v.thumb_url}`} alt={v.title}
                                className="w-full h-full object-cover" loading="lazy" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <FilmSlate size={40} weight="duotone" style={{ color: "#F37A24" }} />
                              </div>
                            )}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="w-14 h-14 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
                                style={{ background: "rgba(243,122,36,0.95)" }}>
                                <Play size={24} weight="fill" className="text-white ml-0.5" />
                              </span>
                            </div>
                            {v.duration ? (
                              <span className="absolute bottom-2 right-2 text-[0.7rem] font-mono font-semibold text-white px-1.5 py-0.5 rounded" style={{ background: "rgba(11,26,47,0.8)" }}>
                                {Math.floor((v.duration || 0) / 60)}:{String(Math.round((v.duration || 0) % 60)).padStart(2, "0")}
                              </span>
                            ) : null}
                            {videoDone.has(v.slug) && (
                              <span className="absolute top-2 left-2"><CheckCircle size={22} weight="fill" className="text-positive drop-shadow" /></span>
                            )}
                            {v.percent > 0 && v.percent < 98 && (
                              <span className="absolute bottom-0 left-0 h-1.5" data-testid={`video-progress-${v.slug}`}
                                style={{ width: `${v.percent}%`, background: "#F37A24" }} />
                            )}
                            {v.updated_recently && (
                              <span data-testid={`video-updated-${v.slug}`}
                                className="absolute top-2 right-2 inline-flex items-center gap-1 text-[0.6rem] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full text-white shadow" style={{ background: "#00A859" }}>
                                <Sparkle size={9} weight="fill" /> Updated
                              </span>
                            )}
                          </div>
                          <div className="p-4">
                            {v.workflow && (
                              <span className="inline-flex items-center gap-1 text-[0.6rem] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full mb-1.5" style={{ background: "rgba(46,163,253,0.12)", color: "#1D6FB8" }} data-testid={`workflow-badge-${v.slug}`}>
                                <FilmSlate size={10} weight="fill" /> Workflow
                              </span>
                            )}
                            <div className="font-semibold text-navy text-sm leading-snug line-clamp-2">{v.title}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        );
      })()}

      {/* Guided tours */}
      <h2 className="overline text-orangeink mb-3">Guided tours</h2>
      <div className="grid sm:grid-cols-3 gap-3 mb-8" data-testid="learning-tours">
        {TOURS.map((t) => {
          const done = toursDone.has(t.id);
          return (
            <button key={t.id} onClick={() => doTour(t.id)} data-testid={`learning-tour-${t.id}`}
              className="text-left rounded-2xl border border-slate-200 bg-white shadow-card hover:border-orange hover:shadow-card-hover transition-all p-4 group">
              <div className="flex items-center justify-between mb-2">
                <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "rgba(242,111,33,0.1)" }}>
                  <Rocket size={18} weight="duotone" style={{ color: "#F37A24" }} />
                </span>
                {done
                  ? <CheckCircle size={20} weight="fill" className="text-positive" data-testid={`learning-tour-done-${t.id}`} />
                  : <PlayCircle size={20} weight="duotone" className="text-slate-300 group-hover:text-orangeink transition-colors" />}
              </div>
              <div className="font-semibold text-navy text-sm">{t.title}</div>
              <div className="text-xs text-slate-500 mt-0.5">{done ? "Completed — replay any time" : t.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Written guides live in the Help Centre — link across rather than re-listing them here */}
      <h2 className="overline text-orangeink mb-3">How-to guides</h2>
      <button onClick={() => nav("/help")} data-testid="learning-guides-link"
        className="group w-full flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white shadow-card hover:border-orange hover:shadow-card-hover transition-all p-5 text-left mb-4">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(242,111,33,0.1)" }}>
            <BookOpen size={22} weight="duotone" style={{ color: "#F37A24" }} />
          </div>
          <div>
            <h3 className="font-heading font-bold text-navy">Looking for the written how-to guides?</h3>
            <p className="text-slate-600 text-sm mt-0.5">All {TOTAL_GUIDES} step-by-step guides — with search, the full workflow roadmap and a downloadable PDF — live in the Help Centre.</p>
          </div>
        </div>
        <span className="shrink-0 inline-flex items-center gap-2 font-semibold text-orangeink">
          Browse all guides <ArrowRight size={16} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
        </span>
      </button>

      {/* Video popup — plays inside the Learning Centre, download disabled, resumes where you left off */}
      <Dialog open={!!activeVideo} onOpenChange={(o) => { if (!o) closeVideo(); }}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-navy border-navy" data-testid="video-modal">
          {activeVideo && (
            <>
              <DialogTitle className="sr-only">{activeVideo.title}</DialogTitle>
              <DialogDescription className="sr-only">Walkthrough video: {activeVideo.title}</DialogDescription>
              <div className="relative">
                <video
                  ref={videoRef}
                  key={activeVideo.slug}
                  data-testid="video-modal-player"
                  src={`${API}${activeVideo.video_url}`}
                  controls autoPlay playsInline
                  controlsList="nodownload noremoteplayback noplaybackrate"
                  disablePictureInPicture
                  onContextMenu={(e) => e.preventDefault()}
                  onPlay={() => { setEnded(false); onVideoPlay(activeVideo.slug); }}
                  onLoadedMetadata={onLoadedMeta}
                  onTimeUpdate={onTimeUpdate}
                  onPause={onTimeUpdate}
                  onEnded={() => setEnded(true)}
                  className="w-full aspect-video bg-black"
                />
                {ended && nextVideo && (
                  <div className="absolute inset-0 flex items-center justify-center bg-navy/85 backdrop-blur-sm px-6" data-testid="watch-next-overlay">
                    <div className="text-center max-w-sm">
                      <div className="overline text-orange mb-2">Up next</div>
                      <div className="font-heading font-bold text-white text-lg leading-tight">{nextVideo.title}</div>
                      <button onClick={() => playVideo(nextVideo)} data-testid="watch-next-btn"
                        className="inline-flex items-center gap-2 mt-4 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-transform hover:scale-105"
                        style={{ background: "#F37A24" }}>
                        <Play size={16} weight="fill" /> Watch next
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="px-5 py-4">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="font-heading font-bold text-white leading-tight">{activeVideo.title}</div>
                  <NarratedByLottie dark />
                </div>
                {(activeVideo.summary || activeVideo.excerpt) && (
                  <p className="text-white/60 text-sm mt-1" data-testid="video-narrative">{activeVideo.summary || activeVideo.excerpt}</p>
                )}
                {chapters.length > 1 && (
                  <div className="mt-3 pt-3 border-t border-white/10" data-testid="modal-chapters">
                    <div className="overline text-white/40 mb-1.5">Chapters</div>
                    <div className="flex flex-wrap gap-1.5">
                      {chapters.map((c, i) => (
                        <button key={i} onClick={() => seekVideo(c.time)} data-testid={`modal-chapter-${i}`}
                          className="inline-flex items-center gap-1.5 text-xs text-white/80 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg pr-2.5 py-1 pl-1 transition-colors text-left">
                          {c.screen && (
                            <img src={`${API}/learning/screen-thumb/${c.screen}`} alt="" loading="lazy"
                              className="w-11 h-7 rounded object-cover object-top border border-white/10 shrink-0" />
                          )}
                          <span className="font-mono tabular-nums text-orange shrink-0 pl-1">{fmtChapter(c.time)}</span>
                          <span className="truncate max-w-[200px]">{c.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {nextVideo && (
                  <button onClick={() => playVideo(nextVideo)} data-testid="watch-next-inline"
                    className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold text-orange hover:text-white transition-colors">
                    Up next: {nextVideo.title} <ArrowRight size={13} weight="bold" />
                  </button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Page>
  );
}
